import * as Hooks from 'preact/hooks';
import * as BP from '@fixtuur/backpack';
import * as Logging from '../../../../store/actions/log';
import { useDispatch, useSelector } from '../../../../store/redux';
import * as Selections from '../../../../store/selections';
import * as Selectors from '../../../../store/selectors';
import { applyAttachmentSelections } from './furniture';
import { useFurnitureChoices } from './useFurnitureChoices';

type ParticleTreeNode = BP.Render.Particle.Tree.ParticleTreeNode;

/**
 * This is copied and pasted wholesale with its dependencies from dora
 * app/src/components/configuration/useAssembleFurniture
 *
 * Anything dora app/ specific was then removed, and some changes made to make it work.
 *
 * In summary the following changes were made:
 * - pass in particles from the computed configuration rather than making the call internally
 * - hence, no need to return resolveFurnitureChoiceTree at all, we just call it here and use the result
 * (recall that dora app fetches a basket for all visible selection options, we just fetch it for the single
 * chosen one)
 *
 * The intention was to rewrite this for stella in a more understandable form but we ran out of time.
 *
 * TODO: clean things
 */
export function useDoraAssembleFurniture(): {
    furnitureTree?: ParticleTreeNode;
    isLoading: boolean;
} {
    const { furnitureChoices, isFurnitureChoicesLoading } = useFurnitureChoices();

    const [isLoading, setIsLoading] = Hooks.useState<boolean>(true);
    const [furnitureTree, setFurnitureTree] = Hooks.useState<ParticleTreeNode | undefined>(
        undefined,
    );

    const [isAttachmentsLoading, setAttachmentsLoading] = Hooks.useState(false);
    const currentAttachmentSelections = useSelector(Selectors.Selections.attachmentSelections);
    const attachmentProposals = useSelector(Selectors.Selections.attachmentProposals);
    const tenant = useSelector(Selectors.Core.tenant);

    if (attachmentProposals) {
        setAttachmentsLoading(true);
    }

    const dispatch = useDispatch();

    Hooks.useEffect(() => {
        setIsLoading(true);
    }, [furnitureChoices, isFurnitureChoicesLoading]);

    Hooks.useEffect(() => {
        if (
            !furnitureChoices ||
            isFurnitureChoicesLoading ||
            (!isLoading && !isAttachmentsLoading)
        ) {
            return;
        }

        const assembledFurniture = applyAttachmentSelections(
            furnitureChoices.attachmentOptions,
            currentAttachmentSelections ?? {},
            attachmentProposals ?? {},
            furnitureChoices.furnitureChoiceTree,
            tenant!,
        );

        const attachmentSelections = assembledFurniture?.attachmentSelections;

        dispatch(Selections.setAttachmentSelections(attachmentSelections));

        if (assembledFurniture?.furnitureTree) {
            // Does undefined mean loaded here? Unclear, think about later
            setFurnitureTree(assembledFurniture.furnitureTree);
            setAttachmentsLoading(false);
            setIsLoading(false);
        } else {
            dispatch(
                Logging.logWarning(
                    'Could not assemble furniture tree for rendering, unclear if this is a problem',
                    {
                        assembledFurniture,
                    },
                ),
            );
        }
    }, [
        furnitureChoices?.attachmentOptions,
        furnitureChoices?.furnitureChoiceTree,
        isFurnitureChoicesLoading,
        isAttachmentsLoading,
    ]);

    // doesn't look like we need to return furnitureChoices at all, it's used in dora /app
    // configuration to get the basket because there it's an async function that computes
    // choices on the fly for any given selections object.
    // In Stella's case we only have one selections object at a time.
    // Secondly: can return assembledFurniture.attachments
    return {
        furnitureTree,
        isLoading,
    };
}
